/**
 * Navigation link component for internal routing
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { isEmpty } from 'lodash';

// CSS Style for this module
import styles from './navLink.module.scss';

/**
 * Interface for NavLink props
 */
interface NavLinkProps {
	/**
	 * Display name, if not provided, url will be displayed
	 */
	children?: React.ReactNode;
	/**
	 * External link
	 */
	isExternal?: boolean;
	/**
	 * On click
	 */
	onClick?: () => void;
	/**
	 * Desitnation of the anchor
	 */
	href: string;
}
/**
 * The NavLink component allows you to easily customize anchor elements
 * @param {NavLinkProps} Prop
 * @returns  a styled NavLink
 */
export const NavLink = ({ href, children, isExternal = false, onClick }: NavLinkProps) => {
	const router = useRouter();
	const pathname = usePathname();
	const isActive = !isEmpty(router) && pathname === href;

	return (
		<>
			{isActive && <span className={styles['visually-hidden']}>Current page: </span>}
			{isExternal ? (
				<Link
					href={href}
					aria-current={isActive}
					onClick={onClick}
					target="_blank"
					rel="noreferrer"
					className={styles['link']}
				>
					{children}
				</Link>
			) : (
				<Link href={href} aria-current={isActive} onClick={onClick} className={styles['link']}>
					{children}
				</Link>
			)}
		</>
	);
};
